/**
 * Seezeichen als Geometrie: Körper, Farbfolge, Toppzeichen und Feuer.
 *
 * Der Körper entsteht aus wenigen Zylindern und Kegeln, die Farbfolge aus übereinander
 * gestapelten Ringen (waagerechte Bänder) oder aus Sektoren (senkrechte Streifen).
 * Das Feuer ist derselbe Sprite wie bei den Fahrzeugen und läuft über den
 * Kennungs-Parser, damit der Rhythmus stimmt und nicht bloss blinkt.
 */
import {
  AdditiveBlending,
  Box3,
  BufferGeometry,
  CanvasTexture,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  TorusGeometry,
  Vector3,
} from 'three'
import type { BuoyData } from '@/core/schema'
import { parseCharacter, sampleAt, type LightCharacter, type RhythmColour } from '@/core/rhythm'
import { alsZahl } from '@/core/lichtfarben'

const COLOURS: Record<string, number> = {
  red: 0xc62828,
  green: 0x1b8a3a,
  yellow: 0xe8c520,
  black: 0x14181d,
  white: 0xe8eaed,
  blue: 0x1f4bd8,
}

/**
 * Das Feuer eines Seezeichens ist dasselbe Licht wie an einem Fahrzeug, also kommen die
 * Farben aus derselben Quelle (`src/core/lichtfarben.ts`). Violett führen die Regeln für
 * Fahrzeuge nicht, es gibt es nur an Seezeichen; der Wert steht deshalb hier.
 */
const LIGHT_COLOURS: Record<RhythmColour, number> = {
  W: alsZahl('white'),
  R: alsZahl('red'),
  G: alsZahl('green'),
  Y: alsZahl('yellow'),
  Bu: alsZahl('blue'),
  Vi: 0xc07bff,
}

export interface BuoyModel {
  group: Group
  /** Höhe des Feuers über Wasser, Bezugspunkt der Kamera. */
  lightHeightM: number
  character?: LightCharacter
  light?: { core: Sprite; halo: Sprite }
  geometries: BufferGeometry[]
  materials: MeshStandardMaterial[]
}

/** Masse in Metern, an einer Leuchttonne mittlerer Grösse ausgerichtet. */
const BODY_HEIGHT = 2.4
const BODY_RADIUS = 1.1

function material(colour: string): MeshStandardMaterial {
  return new MeshStandardMaterial({ color: COLOURS[colour] ?? 0x888888, roughness: 0.75, metalness: 0.05 })
}

/**
 * Waagerechte Bänder: der Körper wird in so viele Scheiben geteilt, wie Farben da sind.
 * Die Form bleibt dabei die Form der Tonne: bei der Spitztonne läuft die oberste Scheibe
 * spitz zu, bei der Kugeltonne bleibt es eine Kugel.
 */
function bandedBody(model: BuoyModel, buoy: BuoyData, height: number, radius: number): void {
  const n = buoy.colours.length
  if (buoy.shape === 'spherical') {
    for (const [i, colour] of buoy.colours.entries()) {
      const phiStart = (i / n) * Math.PI
      const geometry = new SphereGeometry(radius, 18, 12, 0, Math.PI * 2, phiStart, Math.PI / n)
      const mesh = new Mesh(geometry, material(colour))
      mesh.position.y = height * 0.5
      model.group.add(mesh)
      model.geometries.push(geometry)
      model.materials.push(mesh.material as MeshStandardMaterial)
    }
    return
  }
  const sliceHeight = height / n
  for (const [i, colour] of buoy.colours.entries()) {
    const top = i === 0
    const geometry =
      buoy.shape === 'conical' && top
        ? new ConeGeometry(radius, sliceHeight, 16)
        : new CylinderGeometry(radius, radius, sliceHeight, 16)
    const mesh = new Mesh(geometry, material(colour))
    // Farbe 0 liegt oben.
    mesh.position.y = height - sliceHeight * (i + 0.5)
    model.group.add(mesh)
    model.geometries.push(geometry)
    model.materials.push(mesh.material as MeshStandardMaterial)
  }
}

/** Senkrechte Streifen: nur das Mittefahrwasserzeichen trägt sie, und genau daran erkennt man es. */
function stripedBody(model: BuoyModel, buoy: BuoyData, height: number, radius: number): void {
  const n = Math.max(2, buoy.colours.length * 3)
  for (let i = 0; i < n; i += 1) {
    const colour = buoy.colours[i % buoy.colours.length]!
    const geometry =
      buoy.shape === 'spherical'
        ? new SphereGeometry(radius, 12, 10, (i / n) * Math.PI * 2, (Math.PI * 2) / n)
        : new CylinderGeometry(radius, radius, height, 12, 1, false, (i / n) * Math.PI * 2, (Math.PI * 2) / n)
    const mesh = new Mesh(geometry, material(colour))
    mesh.position.y = buoy.shape === 'spherical' ? height * 0.5 : height * 0.5
    model.group.add(mesh)
    model.geometries.push(geometry)
    model.materials.push(mesh.material as MeshStandardMaterial)
  }
}

function addMesh(model: BuoyModel, geometry: BufferGeometry, colour: string, position: Vector3, rotate = 0): void {
  const mesh = new Mesh(geometry, material(colour))
  mesh.position.copy(position)
  if (rotate) mesh.rotation.z = rotate
  model.group.add(mesh)
  model.geometries.push(geometry)
  model.materials.push(mesh.material as MeshStandardMaterial)
}

/** Toppzeichen. Sie tragen bei den Kardinalzeichen die ganze Aussage. */
function addTopmark(model: BuoyModel, buoy: BuoyData, baseY: number): number {
  const size = BODY_RADIUS * 0.85
  const gap = size * 0.35
  const y = baseY + gap
  switch (buoy.topmark) {
    case 'none':
      return baseY
    case 'cylinder-red':
      addMesh(model, new CylinderGeometry(size * 0.55, size * 0.55, size, 12), 'red', new Vector3(0, y + size / 2, 0))
      return y + size
    case 'can-green':
      addMesh(model, new CylinderGeometry(size * 0.55, size * 0.55, size, 12), 'green', new Vector3(0, y + size / 2, 0))
      return y + size
    case 'cone-green':
      addMesh(model, new ConeGeometry(size * 0.6, size, 12), 'green', new Vector3(0, y + size / 2, 0))
      return y + size
    case 'cone-red':
      addMesh(model, new ConeGeometry(size * 0.6, size, 12), 'red', new Vector3(0, y + size / 2, 0))
      return y + size
    case 'cones-up':
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size / 2, 0))
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size * 1.55, 0))
      return y + size * 2.1
    case 'cones-down':
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size / 2, 0), Math.PI)
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size * 1.55, 0), Math.PI)
      return y + size * 2.1
    case 'cones-base':
      // Spitzen voneinander: unterer Kegel zeigt nach unten, oberer nach oben.
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size / 2, 0), Math.PI)
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size * 1.55, 0))
      return y + size * 2.1
    case 'cones-point':
      // Spitzen zueinander.
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size / 2, 0))
      addMesh(model, new ConeGeometry(size * 0.55, size, 12), 'black', new Vector3(0, y + size * 1.55, 0), Math.PI)
      return y + size * 2.1
    case 'spheres-black':
      addMesh(model, new SphereGeometry(size * 0.5, 14, 10), 'black', new Vector3(0, y + size * 0.5, 0))
      addMesh(model, new SphereGeometry(size * 0.5, 14, 10), 'black', new Vector3(0, y + size * 1.6, 0))
      return y + size * 2.1
    case 'sphere-red':
      addMesh(model, new SphereGeometry(size * 0.55, 14, 10), 'red', new Vector3(0, y + size * 0.55, 0))
      return y + size * 1.1
    case 'cross-yellow': {
      const arm = new TorusGeometry(size * 0.5, size * 0.12, 6, 4)
      addMesh(model, arm, 'yellow', new Vector3(0, y + size * 0.5, 0), Math.PI / 4)
      return y + size
    }
  }
}

let glowTexture: CanvasTexture | undefined

function glow(): CanvasTexture {
  if (glowTexture) return glowTexture
  const size = 96
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('kein 2D-Kontext für das Feuer')
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  for (let i = 0; i <= 10; i += 1) g.addColorStop(i / 10, `rgba(255,255,255,${Math.max(0, (1 - i / 10) ** 3).toFixed(3)})`)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  glowTexture = new CanvasTexture(canvas)
  return glowTexture
}

export function buildBuoy(buoy: BuoyData): BuoyModel {
  const model: BuoyModel = { group: new Group(), lightHeightM: 0, geometries: [], materials: [] }
  const height = buoy.shape === 'spar' ? BODY_HEIGHT * 2.2 : BODY_HEIGHT
  const radius = buoy.shape === 'spar' ? BODY_RADIUS * 0.3 : BODY_RADIUS

  if (buoy.pattern === 'stripes') stripedBody(model, buoy, height, radius)
  else bandedBody(model, buoy, height, radius)

  // Ein Gestell trägt Toppzeichen und Feuer, wie an einer Leuchttonne.
  const hasMast = buoy.shape !== 'spherical' && buoy.shape !== 'barrel'
  if (hasMast) {
    const mast = new CylinderGeometry(radius * 0.12, radius * 0.12, height * 0.75, 8)
    addMesh(model, mast, 'black', new Vector3(0, height + height * 0.3, 0))
  }
  // Das Toppzeichen sitzt auf dem, was wirklich gebaut wurde, nicht auf einer Schätzung.
  const bodyTop = new Box3().setFromObject(model.group).max.y
  const lightY = hasMast ? bodyTop - height * 0.12 : bodyTop
  const top = addTopmark(model, buoy, bodyTop + radius * 0.1)
  model.lightHeightM = Math.max(top, bodyTop)

  if (buoy.character) {
    model.character = parseCharacter(buoy.character)
    const colour = LIGHT_COLOURS[model.character.colours[0] ?? 'W']
    const core = new Sprite(
      new SpriteMaterial({ map: glow(), color: colour, blending: AdditiveBlending, depthWrite: false, depthTest: false, sizeAttenuation: false, transparent: true, fog: false }),
    )
    const halo = new Sprite(
      new SpriteMaterial({ map: glow(), color: colour, blending: AdditiveBlending, depthWrite: false, depthTest: false, sizeAttenuation: false, transparent: true, fog: false }),
    )
    const y = lightY
    core.position.set(0, y, 0)
    halo.position.copy(core.position)
    core.renderOrder = 10
    halo.renderOrder = 9
    model.group.add(halo)
    model.group.add(core)
    model.light = { core, halo }
    model.lightHeightM = Math.max(model.lightHeightM, y)
  }
  // Eine Tonne schwimmt, sie steht nicht auf dem Wasser: der Körper taucht ein Stück ein.
  model.group.position.y = -radius * (buoy.shape === 'spar' ? 1.2 : 0.45)
  return model
}

/** Setzt das Feuer auf den Stand der Kennung zur Zeit t. */
export function updateBuoyLight(model: BuoyModel, timeSec: number, dayFactor: number): number {
  if (!model.light || !model.character) return 0
  const sample = sampleAt(model.character, timeSec)
  const intensity = sample.intensity * (1 - dayFactor * 0.9)
  const visible = intensity > 0.01
  model.light.core.visible = visible
  model.light.halo.visible = visible
  if (visible) {
    const colour = LIGHT_COLOURS[sample.colour ?? model.character.colours[0] ?? 'W']
    ;(model.light.core.material as SpriteMaterial).color.setHex(colour)
    ;(model.light.halo.material as SpriteMaterial).color.setHex(colour)
    ;(model.light.core.material as SpriteMaterial).opacity = Math.min(1, intensity * 1.6)
    ;(model.light.halo.material as SpriteMaterial).opacity = Math.min(0.8, intensity * 0.7)
    model.light.core.scale.setScalar(0.016)
    model.light.halo.scale.setScalar(0.055)
  }
  return intensity
}

export function disposeBuoy(model: BuoyModel): void {
  for (const g of model.geometries) g.dispose()
  for (const m of model.materials) m.dispose()
  model.group.clear()
}
